var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _ChatCursor_chat, _ChatCursor_minTimetoken, _ChatCursor_maxTimetoken, _ChatCursor_loadTimeBeforeCursor, _ChatCursor_loadTimeAfterCursor, _ChatCursor_loadMoreThreshold, _ChatCursor_loadActionsStrategy, _ChatCursor_updateThrottleMs, _ChatCursor_cursor, _ChatCursor_onUpdate, _ChatCursor_currentlyLoadingTimetokens;
import { convertToPubNubTimetoken, getPubNubTimestamp } from "./types";
import { abs, max, min } from "./util/bigIntMath";
import { throttleTrailing } from "./util/debounce";
/**
 * A "cursor" for use in chat replay scenarios, when we want to continuously load
 * messages as time advances. The use case in mind is replaying chat for VOD.
 *
 * Broadly, this class serves two functions:
 *  - Holds the state of the cursor (in VOD context, this is the video timestamp,
 *    converted to PubNub timetoken precision)
 *  - Loads more messages on demand, as the cursor position is updated.
 *
 * This class can be configured to load a specified amount of messages before and
 * after the cursor, and can clamp timetokens to given min/max values (in VOD
 * context, this would be the broadcast start and end times).
 */
export class ChatCursor {
    constructor(chat, { minTimestamp = BigInt(0), maxTimestamp, beforeSeconds = 60 * 20, 
    // TODO: These defaults need some more thought. In combination with maxChatMessages,
    // they can pose a problem, depending on how busy chat is and how they are configured.
    afterSeconds = 60, loadMoreThresholdSeconds = 30, loadActionsStrategy = "include", updateThrottleMs = 750, }) {
        _ChatCursor_chat.set(this, void 0);
        _ChatCursor_minTimetoken.set(this, void 0);
        _ChatCursor_maxTimetoken.set(this, void 0);
        _ChatCursor_loadTimeBeforeCursor.set(this, void 0);
        _ChatCursor_loadTimeAfterCursor.set(this, void 0);
        _ChatCursor_loadMoreThreshold.set(this, void 0);
        _ChatCursor_loadActionsStrategy.set(this, void 0);
        _ChatCursor_updateThrottleMs.set(this, void 0);
        // TODO: This could probably safely be just an epoch timestamp.
        // Also, we could make the precision configurable, to throttle
        // cursor updates.
        _ChatCursor_cursor.set(this, void 0);
        _ChatCursor_onUpdate.set(this, void 0);
        // Keep track of any ongoing loads from cursor updates. Otherwise, if the cursor
        // updates frequently, it may trigger a load for a range of messages which is already
        // being fetched.
        _ChatCursor_currentlyLoadingTimetokens.set(this, []);
        this.getCursorTimetoken = () => {
            return __classPrivateFieldGet(this, _ChatCursor_cursor, "f");
        };
        this.onUpdate = (callback) => {
            __classPrivateFieldSet(this, _ChatCursor_onUpdate, throttleTrailing(callback, __classPrivateFieldGet(this, _ChatCursor_updateThrottleMs, "f")), "f");
        };
        this.offUpdate = () => {
            __classPrivateFieldSet(this, _ChatCursor_onUpdate, undefined, "f");
        };
        /**
         * Sets the cursor position and fetches more chat messages if required.
         *
         * If `timestamp` is a `number` type, it is treated as an epoch timestamp
         * and then converted to PubNub timetoken precision.
         */
        this.updateCursor = (timestamp) => {
            const cursor = convertToPubNubTimetoken(timestamp);
            const previousCursor = __classPrivateFieldGet(this, _ChatCursor_cursor, "f");
            if (cursor === previousCursor) {
                return Promise.resolve();
            }
            __classPrivateFieldSet(this, _ChatCursor_cursor, cursor, "f");
            if (__classPrivateFieldGet(this, _ChatCursor_onUpdate, "f")) {
                __classPrivateFieldGet(this, _ChatCursor_onUpdate, "f").call(this, cursor);
            }
            const loadedRangeIntersectingCursor = __classPrivateFieldGet(this, _ChatCursor_chat, "f").getLoadedRangeIntersecting(cursor);
            const isCurrentlyLoadingForCursor = __classPrivateFieldGet(this, _ChatCursor_currentlyLoadingTimetokens, "f").some((timetoken) => abs(timetoken - cursor) < __classPrivateFieldGet(this, _ChatCursor_loadMoreThreshold, "f"));
            const isEnoughLoadedBeforeCursor = loadedRangeIntersectingCursor &&
                (loadedRangeIntersectingCursor.end <= __classPrivateFieldGet(this, _ChatCursor_minTimetoken, "f") ||
                    cursor - loadedRangeIntersectingCursor.end >=
                        __classPrivateFieldGet(this, _ChatCursor_loadTimeBeforeCursor, "f"));
            const isEnoughLoadedAfterCursor = loadedRangeIntersectingCursor &&
                loadedRangeIntersectingCursor.start - cursor >= __classPrivateFieldGet(this, _ChatCursor_loadMoreThreshold, "f");
            if (isCurrentlyLoadingForCursor ||
                (isEnoughLoadedBeforeCursor && isEnoughLoadedAfterCursor)) {
                return Promise.resolve();
            }
            __classPrivateFieldGet(this, _ChatCursor_currentlyLoadingTimetokens, "f").push(cursor);
            const loadRangeStart = cursor + __classPrivateFieldGet(this, _ChatCursor_loadTimeAfterCursor, "f");
            return __classPrivateFieldGet(this, _ChatCursor_chat, "f")
                .loadMessages({
                type: "range",
                loadActionsStrategy: __classPrivateFieldGet(this, _ChatCursor_loadActionsStrategy, "f"),
                end: max(cursor - __classPrivateFieldGet(this, _ChatCursor_loadTimeBeforeCursor, "f"), __classPrivateFieldGet(this, _ChatCursor_minTimetoken, "f")),
                start: __classPrivateFieldGet(this, _ChatCursor_maxTimetoken, "f")
                    ? min(loadRangeStart, __classPrivateFieldGet(this, _ChatCursor_maxTimetoken, "f"))
                    : loadRangeStart,
            })
                .finally(() => {
                __classPrivateFieldSet(this, _ChatCursor_currentlyLoadingTimetokens, __classPrivateFieldGet(this, _ChatCursor_currentlyLoadingTimetokens, "f").filter((timetoken) => timetoken !== cursor), "f");
            });
        };
        __classPrivateFieldSet(this, _ChatCursor_chat, chat, "f");
        console.assert(loadMoreThresholdSeconds < afterSeconds &&
            loadMoreThresholdSeconds < beforeSeconds, "The value of 'loadMoreThresholdSeconds' must be less than both 'beforeSeconds' and " +
            "'afterSeconds'. Otherwise, a load could be triggered on every cursor update. " +
            "If you want more frequent updates, reduce the value(s) of '{before|after}Seconds'.");
        console.assert(beforeSeconds >= 0, "Expected beforeSeconds >= 0");
        console.assert(afterSeconds >= 0, "Expected afterSeconds >= 0");
        const minTimetoken = convertToPubNubTimetoken(minTimestamp);
        const maxTimetoken = convertToPubNubTimetoken(maxTimestamp);
        console.assert(maxTimetoken === undefined || maxTimetoken > minTimetoken, "Expected maxTimetoken > minTimetoken");
        __classPrivateFieldSet(this, _ChatCursor_minTimetoken, minTimetoken, "f");
        __classPrivateFieldSet(this, _ChatCursor_maxTimetoken, maxTimetoken, "f");
        __classPrivateFieldSet(this, _ChatCursor_loadTimeBeforeCursor, getPubNubTimestamp(beforeSeconds * 1000), "f");
        __classPrivateFieldSet(this, _ChatCursor_loadTimeAfterCursor, getPubNubTimestamp(afterSeconds * 1000), "f");
        __classPrivateFieldSet(this, _ChatCursor_loadMoreThreshold, getPubNubTimestamp(loadMoreThresholdSeconds * 1000), "f");
        __classPrivateFieldSet(this, _ChatCursor_loadActionsStrategy, loadActionsStrategy, "f");
        __classPrivateFieldSet(this, _ChatCursor_updateThrottleMs, updateThrottleMs, "f");
    }
}
_ChatCursor_chat = new WeakMap(), _ChatCursor_minTimetoken = new WeakMap(), _ChatCursor_maxTimetoken = new WeakMap(), _ChatCursor_loadTimeBeforeCursor = new WeakMap(), _ChatCursor_loadTimeAfterCursor = new WeakMap(), _ChatCursor_loadMoreThreshold = new WeakMap(), _ChatCursor_loadActionsStrategy = new WeakMap(), _ChatCursor_updateThrottleMs = new WeakMap(), _ChatCursor_cursor = new WeakMap(), _ChatCursor_onUpdate = new WeakMap(), _ChatCursor_currentlyLoadingTimetokens = new WeakMap();
